import React from 'react';
import PropTypes from 'prop-types';
import withRouteContext from 'tuc-webui-base/components/utils/withRouteContext';
import { TABLE_VIEW_TYPE } from 'tuc-webui-base/services/ViewService';
import SearchBar from 'tuc-webui-base/components/SearchBar';
import FilterBar from '../FilterBar';
import fromConfiguration from '../../configuration/fromConfiguration';
import TableWithOverview from './TableWithOverview';
import './TablePanel.scss';

class TablePanel extends React.PureComponent {
  static getStorageItem(props, key) {
    const { context } = props;
    if (context.getStorageItem) {
      return context.getStorageItem(key);
    }
    return window.localStorage.getItem(key);
  }

  static setStorageItem(props, key, item) {
    const { context } = props;
    if (context.setStorageItem) {
      context.setStorageItem(key, item);
    } else {
      window.localStorage.setItem(key, item);
    }
  }

  static saveSelection(props, selected, storageKey) {
    if (selected.length) {
      const { id } = selected[0];
      TablePanel.setStorageItem(props, storageKey, id);
    }
  }

  static getDerivedStateFromProps(props, state = { toSelect: {}, selectionStorageKey: null }) {
    const { configuration, rowIdToSelect, saveSelection } = props;
    const rows = configuration.rows ? props[configuration.rows] : props.rows;
    const rowCount = rows && (Array.isArray(rows) ? rows.length : rows.totalCount);
    if (rows && (state.filteredRowCountProperty || state.rowCountProperty) && !rowCount) {
      if (state.rowCountProperty === state.filteredRowCountProperty) {
        state.rowCountProperty.setRowNumbers([0, 0]);
      } else {
        if (state.rowCountProperty) {
          state.rowCountProperty.setTotalRowCount(0);
        }
        if (state.filteredRowCountProperty) {
          state.filteredRowCountProperty.setFilteredRowCount(0);
        }
      }
    }
    if (rowIdToSelect !== state.toSelect.last) {
      // state.toSelect is constant, no need to set it to a new state
      state.toSelect.last = rowIdToSelect;
      state.toSelect.id = rowIdToSelect;
    }
    let newState = null;
    if (state.rows !== rows) {
      newState = {
        tableModels: [
          Object.assign({}, state.tableModels[0], { data: rows }),
        ],
        rows,
      };
    }
    // Manage selection
    if (state.toSelect.id) {
      const { getRowId } = state.tableModels[0];
      const { id } = state.toSelect;
      const sel = rows && (Array.isArray(rows) ? rows : rows.data).find(o => getRowId(o) === id);
      if (sel) {
        state.toSelect.id = undefined;
        newState = Object.assign({ selected: [sel] }, newState);
        if (saveSelection) {
          TablePanel.saveSelection(props, [sel], state.selectionStorageKey);
        }
      }
    }

    if (state.detailsComponent !== props.detailsComponent) {
      newState = Object.assign(newState || {}, {
        detailsComponent: props.detailsComponent,
        DetailsComponent: fromConfiguration(props.detailsComponent, props),
      });
    }

    if (state.overviewComponent !== props.overviewComponent) {
      newState = Object.assign(newState || {}, {
        overviewComponent: props.overviewComponent,
        OverviewComponent: fromConfiguration(props.overviewComponent, props),
      });
    }

    return newState;
  }

  constructor(props) {
    super(props);

    const { configuration = {}, viewService, getStorageKey, saveSelection } = props;
    this.TableComponent = viewService.getViewComponent(configuration.tableComponent || TABLE_VIEW_TYPE);
    this.getObjectId = props.getObjectId || (props.configuration.idProperty && props[props.configuration.idProperty]) || (obj => obj.id);
    const getTableRowId = obj => `${configuration.entityName}-row-${this.getObjectId(obj)}`;
    const model = Object.assign({
      readOnly: true,
      canCheck: false,
      canSelect: true,
      canCreate: false,
      canDelete: false,
      reuseSameObjects: true,
    }, configuration.model, props.tableModel);

    this.state = {
      tableModels: [{
        ...configuration.data,
        ...model,
        getRowId: this.getObjectId,
        getExtraFilterValues: props.getExtraFilterValues || model.getExtraFilterValues,
        onLoadPage: configuration.table && configuration.table.onLoadPageProperty
              && props[configuration.table.onLoadPageProperty],
      }],
      filteredRowCountProperty: configuration.table && configuration.table.filteredRowCountProperty
                                        && props[configuration.table.filteredRowCountProperty],
      rowCountProperty: configuration.table && configuration.table.rowCountProperty
                                        && props[configuration.table.rowCountProperty],
      toSelect: {},
      selectionStorageKey: getStorageKey(configuration.storagePrefixKey || configuration.entityName, 'select'),
    };
    if (saveSelection) {
      const selectId = TablePanel.getStorageItem(props, this.state.selectionStorageKey);
      const rows = configuration.rows ? props[configuration.rows] : props.rows;
      const sel = selectId && rows && (Array.isArray(rows) ? rows : rows.data)
        .find(o => this.getObjectId(o) === selectId);
      if (sel) {
        this.state.selected = [sel];
      }
    }
    // Configuration for the table
    this.tableConfig = Object.assign(
      {
        ctnrClassName: 'grb-table-ctnr',
      },
      configuration.table,
      {
        storageKey: getStorageKey(configuration.storagePrefixKey || configuration.entityName, 'table'),
        getTableRowId,
        rowsPerPage: configuration.table.rowsPerPage || -1,
        noRowsMessage: configuration.noRowsMessage === false ? false : (configuration.noRowsMessage || `No ${configuration.entityName}s`),
      },
    );
    if (this.tableConfig.delete && (typeof this.tableConfig.delete.icon === 'string')) {
      this.tableConfig.delete.icon = viewService.getIcon(this.tableConfig.delete.icon);
    }
    if (this.tableConfig.add && (typeof this.tableConfig.add.icon === 'string')) {
      this.tableConfig.add.icon = viewService.getIcon(this.tableConfig.add.icon);
    }

    if (configuration.toolbar && configuration.toolbar.trailingComponent) {
      this.ToolbarComponent = viewService.getComponent(configuration.toolbar.trailingComponent);
    }

    // Bindings
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
  }

  handleFilterChange(filter) {
    const newContext = Object.assign({}, this.props.context, { filter });
    this.props.onContextChange(newContext);
  }

  handleSelect(selected) {
    TablePanel.saveSelection(this.props, selected, this.state.selectionStorageKey);
    this.setState({ selected });
  }

  render() {
    const { context, overview } = this.props;
    const { tableModel, filterEditor, configuration, ...rest } = this.props;
    const { entityName } = configuration;
    const { rows, DetailsComponent, OverviewComponent, tableModels } = this.state;
    const searchConfig = (configuration.toolbar || {}).search;
    const TableComponent = this.props.tableComponent || this.TableComponent;
    return (
      <div className={`grb-tablepanel grb-${configuration.entityName}-tablepanel`}>
        {configuration.toolbar && (
          <div className={`grb-tablepanel-toolbar grb-${configuration.entityName}-tablepanel-toolbar`}>
            <SearchBar
              {...rest}
              className="grb-table-search"
              placeholder={searchConfig && searchConfig.placeholder || `Search ${entityName}s...`}
              disabled={!rows || !Array.isArray(rows) || (rows.length === 0)}
            />
            {(filterEditor !== undefined) && (
              <FilterBar
                {...rest}
                filter={context && context.filter || {}}
                onFilterChange={this.handleFilterChange}
                filterEditor={filterEditor}
              />
            )}
            {(this.ToolbarComponent !== undefined) && (
              <this.ToolbarComponent {...this.props} onFilterChange={this.handleFilterChange} />
            )}
          </div>
        )}
        <div className={`grb-tablepanel-body grb-${entityName}-body`}>
          <TableComponent
            {...this.props}
            configuration={this.tableConfig}
            models={tableModels}
            selected={this.props.selected || this.state.selected}
            onSelect={this.props.onSelect || (this.props.saveSelection ? this.handleSelect : undefined)}
            MasterComponent={overview ? TableWithOverview : undefined}
            OverviewComponent={overview ? OverviewComponent : undefined}
            DetailsComponent={DetailsComponent}
            ensureSelection
          />
        </div>
      </div>
    );
  }
}

TablePanel.propTypes = {
  configuration: PropTypes.shape({
    noRowsMessage: PropTypes.string,
    noSelectionMessage: PropTypes.string,
    multiSelectionMessage: PropTypes.string,
    storagePrefixKey: PropTypes.string,
    entityName: PropTypes.string,
    idProperty: PropTypes.string,
    table: PropTypes.shape({
      filteredRowCountProperty: PropTypes.string,
      rowCountProperty: PropTypes.string,
    }),
    toolbar: PropTypes.shape({}),
    rows: PropTypes.string,
  }),
  overview: PropTypes.shape({}),
  detailsComponent: PropTypes.shape({}),
  masterComponent: PropTypes.shape({}),
  overviewComponent: PropTypes.shape({}),
  viewService: PropTypes.shape({
    getViewComponent: PropTypes.func,
    getComponent: PropTypes.func,
    getIcon: PropTypes.func,
  }).isRequired,
  route: PropTypes.shape({}).isRequired,
  context: PropTypes.shape({
    filter: PropTypes.shape({}),
  }),
  onContextChange: PropTypes.func.isRequired,
  getStorageKey: PropTypes.func.isRequired,
  getObjectId: PropTypes.func,
  filterEditor: PropTypes.shape({}),
  getExtraFilterValues: PropTypes.func,
  tableModel: PropTypes.shape({}),
  tableComponent: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({}),
  ]),
  selected: PropTypes.arrayOf(PropTypes.shape({})),
  onSelect: PropTypes.func,
  saveSelection: PropTypes.bool,
  rowIdToSelect: PropTypes.string,
  rows: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({})),
      totalCount: PropTypes.number,
    }),
  ]),
};

TablePanel.defaultProps = {
  configuration: {},
  overview: undefined,
  context: undefined,
  getObjectId: undefined,
  filterEditor: undefined,
  getExtraFilterValues: undefined,
  detailsComponent: undefined,
  masterComponent: undefined,
  overviewComponent: undefined,
  tableModel: undefined,
  tableComponent: undefined,
  selected: undefined,
  onSelect: undefined,
  saveSelection: false,
  rowIdToSelect: undefined,
  rows: undefined,
};

export default withRouteContext(TablePanel);
